import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react';
import Image from 'next/image';
import {
  Avatar,
  Box,
  Button,
  InputArea,
  Tooltip,
  Text,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  AttachmentStatus,
  TimelineItemType,
  TimelineReply,
  User,
} from '@wix/answers-api';
import DismissSmallIcon from 'wix-ui-icons-common/DismissSmall';
import { UploadType } from '../../../types';
import { UploadModal } from './UploadModal';
import { DATA_HOOKS } from '../../../dataHooks';
import { Context } from '../../../context';
import css from './index.module.scss';

type ReplyProps = {
  user: User;
  ticketId: string;
  onReplyAdded: (timelineItem: TimelineReply) => void;
};

export type AttachmentCandidate = {
  filename: string;
  filelink: string;
};

export const Reply: FunctionComponent<ReplyProps> = ({
  user,
  ticketId,
  onReplyAdded,
}: ReplyProps) => {
  const [showUploadModal, setShowUploadModal] = useState<UploadType | null>(
    null
  );
  const [attachmentCandidate, setAttachmentCandidate] =
    useState<AttachmentCandidate | null>(null);
  const [content, setContent] = useState('');
  const [userId, setUserId] = useState('');
  const { answersApi } = useContext(Context);
  const { t } = useTranslation();

  const replyTicket = async () => {
    try {
      const attachments = attachmentCandidate
        ? [
            {
              name: attachmentCandidate?.filename as string,
              url: attachmentCandidate?.filelink as string,
              status: AttachmentStatus.OK,
            },
          ]
        : [];
      if (content.length > 0 || attachments.length > 0) {
        const reply = await answersApi.tickets.replyTicketHelpCenter({
          content,
          internal: false,
          id: ticketId,
          attachments,
        });
        setAttachmentCandidate(null);
        setContent('');
        onReplyAdded({
          timelineItemType: TimelineItemType.REPLY,
          ...reply,
        });
      }
    } catch {
      alert(t('ticket.reply.error'));
    }
  };

  useEffect(() => {
    void (async () => {
      const { id } = await answersApi.users.me();
      setUserId(id);
    })();
  }, []);

  return userId === user.id ? (
    <Box
      direction="vertical"
      gap="15px"
      className={`${css.wrapper}`}
      dataHook={DATA_HOOKS.REPLY}
    >
      <Box className={css.avatarWrapper} verticalAlign="middle" gap="14px">
        <Avatar
          name={user.firstName}
          imgProps={{ src: user.profileImage }}
          className={css.avatar}
        />
        <Text dataHook={DATA_HOOKS.REPLY_FIRST_NAME}>{user.firstName}</Text>
      </Box>
      <Box className={css.inputAreaWrapper}>
        <div className={css.triangle} />
        <InputArea
          dataHook={DATA_HOOKS.REPLY_INPUT_AREA}
          placeholder={t('ticket.reply.input.placeholder')}
          className={css.inputArea}
          minHeight="160px"
          forceHover
          autoGrow
          onChange={(e) => setContent(e.target.value)}
          value={content}
        ></InputArea>
        <Tooltip
          className={css.uploadImageIconTooltip}
          appendTo="parent"
          content={t('ticket.reply.upload.modal.tooltip.image')}
          size="small"
          placement="bottom"
          enterDelay={300}
        >
          <div
            data-hook={DATA_HOOKS.REPLY_IMAGE_ICON}
            onClick={() => setShowUploadModal(UploadType.IMAGE)}
          >
            <Image
              src={'https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/image-attachment.svg'}
              alt=""
              width="11"
              height="9"
              className={css.uploadImageIcon}
              loading="eager"
            />
          </div>
        </Tooltip>
        <Tooltip
          className={css.uploadFileIconTooltip}
          appendTo="parent"
          content={t('ticket.reply.upload.modal.tooltip.file')}
          size="small"
          enterDelay={300}
          placement="bottom"
        >
          <div
            data-hook={DATA_HOOKS.REPLY_FILE_ICON}
            onClick={() => setShowUploadModal(UploadType.FILE)}
          >
            <Image
              src={'https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/file-attachment.svg'}
              width="9"
              height="8"
              alt=""
              className={css.uploadFileIcon}
              loading="eager"
            />
          </div>
        </Tooltip>
      </Box>
      <Box
        className={`${css.wrapperSubmit} ${
          attachmentCandidate && css.wrapperSubmitWithAttachment
        } `}
      >
        {attachmentCandidate && (
          <Box className={css.uploadedFile}>
            <Text size="small" className={css.uploadedFileText} ellipsis>
              {attachmentCandidate?.filename}
            </Text>
            <DismissSmallIcon
              className={css.uploadedFileIcon}
              onClick={() => setAttachmentCandidate(null)}
            />
          </Box>
        )}
        <Button
          className={css.submitButton}
          size="large"
          onClick={replyTicket}
          dataHook={DATA_HOOKS.REPLY_SUBMIT}
        >
          {t('ticket.reply.submit')}
        </Button>
      </Box>
      <UploadModal
        isOpen={showUploadModal !== null}
        hideModal={() => {
          setShowUploadModal(null);
        }}
        uploadType={showUploadModal as UploadType}
        setAttachmentCandidate={setAttachmentCandidate}
      />
    </Box>
  ) : null;
};
